import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Save, Eye, Loader2 } from "lucide-react";
import api from "../../lib/api";

export default function EmailTemplatesTab() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const loadList = async () => {
    try {
      const res = await api.get("/admin/email-templates");
      setItems(res.data.items || []);
      if (!active && res.data.items?.length) setActive(res.data.items[0].slug);
    } catch (e) { toast.error("Couldn't load templates"); }
  };

  const loadTemplate = async (slug) => {
    try {
      const res = await api.get(`/admin/email-templates/${slug}`);
      setSubject(res.data.subject || "");
      setBody(res.data.body_html || "");
      setPreviewHtml(null);
    } catch (e) { toast.error("Couldn't load template"); }
  };

  useEffect(() => { loadList(); }, []);
  useEffect(() => { if (active) loadTemplate(active); }, [active]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/email-templates/${active}`, {
        subject, body_html: body,
      });
      toast.success("Template saved.");
      await loadList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const preview = async () => {
    setPreviewing(true);
    try {
      const res = await api.post(`/admin/email-templates/${active}/preview`, {
        subject, body_html: body,
      });
      setPreviewHtml(res.data.html);
    } catch (e) { toast.error("Preview failed"); }
    finally { setPreviewing(false); }
  };

  const activeItem = items.find((i) => i.slug === active) || {};

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4" data-testid="email-templates-tab">
      <div className="lg:col-span-1 border border-navy/10 rounded-lg bg-white overflow-hidden h-fit">
        <div className="px-4 py-3 border-b border-navy/10 bg-navy/[0.02] flex items-center gap-2">
          <Mail size={14} className="text-emerald-700" />
          <div className="overline text-emerald-700">Templates</div>
        </div>
        <ul>
          {items.map((t) => (
            <li key={t.slug}>
              <button
                onClick={() => setActive(t.slug)}
                className={`w-full text-left px-4 py-3 border-b border-navy/[0.05] hover:bg-emerald-50/40 ${
                  active === t.slug ? "bg-emerald-50 text-emerald-800" : "text-ink"
                }`}
                data-testid={`email-template-item-${t.slug}`}
              >
                <div className="font-heading text-sm">{t.slug}</div>
                <div className="text-[11px] text-ink-muted line-clamp-2">{t.description}</div>
              </button>
            </li>
          ))}
          {items.length === 0 && <li className="p-4 text-sm text-ink-muted">No templates yet.</li>}
        </ul>
      </div>

      <div className="lg:col-span-3 space-y-4">
        {active && (
          <>
            <div className="border border-navy/10 rounded-lg bg-white p-5">
              <div className="mb-1 text-[10px] font-mono uppercase tracking-widest text-emerald-700">
                Editing · {active}
              </div>
              <div className="text-xs text-ink-muted mb-4">{activeItem.description}</div>

              <div className="mb-4">
                <label className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">Subject</label>
                <input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full px-3 py-2 border border-navy/15 rounded-md text-sm text-ink focus:outline-none focus:ring-1 focus:ring-emerald-400"
                  data-testid="email-template-subject"
                  maxLength={300}
                />
              </div>

              <div className="mb-3">
                <label className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
                  Body HTML — use {"{"}{"name"}{"}"}, {"{"}{"email"}{"}"}, {"{"}{"role_label"}{"}"}, {"{"}{"app_url"}{"}"} as placeholders
                </label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  className="w-full px-3 py-2 border border-navy/15 rounded-md text-xs font-mono text-ink h-72 resize-y focus:outline-none focus:ring-1 focus:ring-emerald-400"
                  data-testid="email-template-body"
                  maxLength={50000}
                />
                <div className="text-[10px] text-ink-muted mt-1 text-right font-mono">
                  {body.length} / 50000
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={save}
                  disabled={saving}
                  className="px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-heading inline-flex items-center gap-1.5 disabled:opacity-60"
                  data-testid="email-template-save"
                >
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  {saving ? "Saving…" : "Save template"}
                </button>
                <button
                  onClick={preview}
                  disabled={previewing}
                  className="px-4 py-2 rounded-md border border-navy/15 text-ink text-sm hover:bg-navy/[0.04] inline-flex items-center gap-1.5"
                  data-testid="email-template-preview"
                >
                  <Eye size={14} /> {previewing ? "Rendering…" : "Preview"}
                </button>
              </div>
            </div>

            {previewHtml && (
              <div className="border border-navy/10 rounded-lg bg-white p-5" data-testid="email-template-preview-frame">
                <div className="overline text-emerald-700 mb-3">Preview</div>
                <iframe
                  title="Email preview"
                  srcDoc={previewHtml}
                  className="w-full h-[520px] border border-navy/10 rounded"
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
